import { Button, InputForm, Paginator, TableGeneric } from '@components/index'
import { defaultPagination } from '@components/paginator/paginator-utils'
import { IContacts } from '@interfaces/contacts/IContacts'
import { IContactsFilters } from '@interfaces/contacts/IContactsFilters'
import { IColumn, IListPaginated } from '@interfaces/index'
import { Box, Modal } from '@mui/material'
import { errorHandler } from '@services/index'
import ContactsService from '@services/requests/contactService'
import { formatDate } from '@utils/dates/dateUtils'
import { phoneMask, totalPages } from '@utils/index'
import axios from 'axios'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AiOutlineCheckCircle, AiOutlineClose, AiOutlineInfoCircle, AiOutlineSearch } from 'react-icons/ai'
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'

export default function Contacts(): React.ReactElement {

    const queryPage = useQuery().get('page')
    const search = useQuery().get('search') as string
    function useQuery(): URLSearchParams {
        return new URLSearchParams(useLocation().search)
    }

    const navigate = useNavigate()
    const [data, setData] = React.useState<IListPaginated<IContacts>>(defaultPagination)
    const [isLoading, setIsLoading] = React.useState(false)
    const [isLoadingDelete, setIsLoadingDelete] = React.useState(false)
    const [isLoadingDownload, setIsLoadingDownload] = React.useState(false)

    const [currentItem, setCurrentItem] = React.useState<IContacts | null>(null)

    const [isOpen, setIsOpen] = React.useState(false)

    const { handleSubmit, control } = useForm<IContactsFilters>({
        defaultValues: {
            search: search ?? ''
        }
    })

    const columns: IColumn<IContacts>[] = React.useMemo<IColumn<IContacts>[]>(
        () => [
            {
                header: 'Data',
                dataIndex: 'createdAt',
                paddingRight: '15px',
                render: (item) => (
                    <div style={{ fontWeight: !item.visualizedAt ? 'bold' : 'normal' }}>
                        {formatDate(item.createdAt)}
                    </div>
                )
            },
            {
                header: 'Nome',
                dataIndex: 'name',
                paddingRight: '15px',
                render: (item) => (
                    <div style={{ fontWeight: !item.visualizedAt ? 'bold' : 'normal' }}>
                        {item.name}
                    </div>
                )
            },
            {
                header: 'E-mail',
                dataIndex: 'email',
                minWidth: '120px',
                paddingRight: '15px',
                render: (item) => (
                    <div style={{ fontWeight: !item.visualizedAt ? 'bold' : 'normal' }}>
                        {item.email}
                    </div>
                )
            },
            {
                header: 'Departamento',
                dataIndex: 'department',
                size: '250px',
                paddingRight: '15px',
                render: (item) => (
                    <div style={{ fontWeight: !item.visualizedAt ? 'bold' : 'normal' }}>
                        {getTranslatedDepartment(item.department)}
                    </div>
                )
            },
            {
                header: 'Visualizado',
                dataIndex: 'visualizedAt',
                size: '50px',
                align: 'align-center',
                render: (item) => (
                    <div>
                        {
                            item.visualizedAt
                                ? <AiOutlineCheckCircle
                                    className='check-circle'
                                    data-tooltip-id={`visualized-tooltip-${item.id}`} data-tooltip-content={`Visualizado em ${formatDate(item.visualizedAt, 'dd/MM/yyyy \'às\' HH:mm')}`} />
                                : <AiOutlineInfoCircle
                                    className='info-circle'
                                    data-tooltip-id={`visualized-tooltip-${item.id}`} data-tooltip-content="Não visualizado" />
                        }
                        <Tooltip
                            id={`visualized-tooltip-${item.id}`}
                            place="bottom"
                            opacity={100}
                            className="visualized-tooltip" />
                    </div>
                )
            }
        ],
        [data]
    )

    // Função para mapear os departamentos
    const getTranslatedDepartment = (department: string): string => {
        switch (department) {
            case 'I_WANT_TO_BE_A_SUPPLIER':
                return 'Quero ser um fornecedor'
            case 'I_WANT_TO_SEND_A_RESUME':
                return 'Quero enviar um currículo'
            case 'I_AM_A_REALTOR_AND_I_WANT_TO_REGISTER':
                return 'Sou corretor de imóveis e quero me inscrever'
            case 'I_WANT_TO_TALK_TO_CNA':
                return 'Quero falar com a CNA'
            case 'I_AM_INTERESTED':
                return 'Tenho interesse'
            default:
                return department
        }
    }

    const renderRealEstateLink = (id: string, type: string): string => {
        const siteUrl = process.env.REACT_APP_SITE_URL as string
        const realEstateType = type === 'VENTURE' ? 'empreendimento' : 'lancamento'
        return `${siteUrl}/${realEstateType}/${id}`
    }

    function init(): void {
        if (queryPage)
            getList(parseInt(queryPage), undefined, search)
        else
            getList(1)
    }

    React.useEffect(() => {
        init()
    }, [queryPage, search])

    async function getList(page: number, perPage?: number, search?: string): Promise<void> {
        setIsLoading(true)
        try {
            const response = await ContactsService.list(page, perPage, search)
            if (page !== 1 && response.items.length <= 0) {
                navigate(`?page=${page - 1}`)
            } else {
                setData(response)
            }
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    async function deleteContact(id: number | undefined): Promise<void> {
        setIsLoadingDelete(true)
        if (id) {
            try {
                const response = await ContactsService.delete(id)
                toast(response.message, { type: 'success' })
                init()
                handleClose()
            } catch (err) {
                toast(errorHandler(err).message, { type: 'error' })
            } finally {
                setIsLoadingDelete(false)
            }
        }
    }

    async function fileKeyDownload(id: number, fileKey: string): Promise<void> {
        try {
            setIsLoadingDownload(true)
            const apartmentUrl = await ContactsService.getFileKey(id)
            const response = await axios({
                method: 'GET',
                url: apartmentUrl,
                responseType: 'blob',
            })
            const url = window.URL.createObjectURL(response.data)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', fileKey)
            document.body.appendChild(link)
            link.click()
            link.remove()
        } catch (error) {
            toast(errorHandler(error).message, { type: 'error' })
        } finally {
            setIsLoadingDownload(false)
        }
    }

    function submit(values: IContactsFilters): void {
        navigate(
            {
                pathname: '/contatos',
                search: createSearchParams({
                    page: '1',
                    search: values.search,
                }).toString()
            }
        )
    }

    function handleClose(): void {
        return setIsOpen(false)
    }

    async function handleOpen(item: IContacts): Promise<void> {
        try {
            setCurrentItem(item)
            if (!item?.visualizedAt) {
                await ContactsService.visualize(parseInt(item.id))
                setData(prevState => {
                    for (const contact of prevState.items) {
                        if (contact.id === item.id) {
                            contact.visualizedAt = new Date()
                        }
                    }

                    return prevState
                })
            }
        } catch (error) {
            toast(errorHandler(error).message, { type: 'error' })
        } finally {
            setIsOpen(true)
        }
    }

    return (
        <div className="card">
            <div className="contacts">
                <div className='page-header'>
                    <h4 className='title'>E-mails de Contato</h4>
                </div>
                <form className='contacts-filters' onSubmit={handleSubmit(submit)}>
                    <Controller
                        control={control}
                        name='search'
                        render={({ field: { value, onChange } }) => (
                            <InputForm
                                size="small"
                                value={value}
                                onChange={(e) => { onChange(e) }}
                                onInputCallBack={() => {
                                    handleSubmit(submit)()
                                }}
                                placeholder="Buscar por Nome ou E-mail"
                                style={{ width: '23rem', marginRight: '1rem' }}
                                InputProps={{
                                    endAdornment: (
                                        <button type='submit' className='button-search'>
                                            <AiOutlineSearch />
                                        </button>
                                    )
                                }}
                            />
                        )}
                    />
                </form>
                <div>
                    <TableGeneric data={data.items} columns={columns} onLineClick={handleOpen} isLoading={isLoading} />
                    <div className="paginator">
                        <Paginator
                            maxElements={7}
                            current={data.page}
                            total={totalPages(data.count, data.perPage)}
                            onClick={(page) => navigate(
                                {
                                    pathname: '/contatos',
                                    search: createSearchParams({
                                        page: page.toString(),
                                        search: search ? search : ''
                                    }).toString()
                                }
                            )} />
                    </div>
                </div>
                <Modal
                    open={isOpen}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                >
                    <Box>
                        <div className="container-modal-contact">
                            <button onClick={() => setIsOpen(false)} className='btnClose'>
                                <AiOutlineClose />
                            </button>
                            <div className="wrapper-header">
                                <h4 id="parent-modal-title">E-mail de Contato - Detalhes</h4>
                            </div>
                            <div className="wrapper-infos">
                                <div className="infos-1">
                                    <div className="info-nome">
                                        <h5>Nome</h5>
                                        <p>{currentItem && currentItem.name}</p>
                                    </div>
                                    <div className="info-data">
                                        <h5>Data</h5>
                                        <p>{currentItem && formatDate(currentItem.createdAt)}</p>
                                    </div>
                                </div>
                                <div className="infos-2">
                                    <div className="info-email">
                                        <h5>E-mail</h5>
                                        <p>{currentItem && currentItem.email}</p>
                                    </div>
                                    <div className="info-telefone">
                                        <h5>Telefone</h5>
                                        <p>{currentItem && phoneMask(currentItem.phone)}</p>
                                    </div>
                                </div>
                                <div className="infos-3">
                                    <div className="info-cidade">
                                        <h5>Cidade</h5>
                                        <p>{currentItem && currentItem.city}</p>
                                    </div>
                                    <div className="info-uf">
                                        <h5>UF</h5>
                                        <p>{currentItem && currentItem.uf}</p>
                                    </div>
                                </div>
                                <div className="infos-4">
                                    <div className="info-departamento">
                                        <h5>Departamento</h5>
                                        <p>{currentItem && getTranslatedDepartment(currentItem.department)}</p>
                                    </div>
                                    {currentItem && currentItem.department === 'I_WANT_TO_SEND_A_RESUME' && currentItem.fileKey && (
                                        <div className="info-file">
                                            <h5>Anexo de Currículo</h5>
                                            <div className='divDownload'>
                                                <Button isLoading={isLoadingDownload} type="button" placeholder="Baixar" onClick={() => currentItem?.id && fileKeyDownload(Number(currentItem.id), currentItem.fileKey)} />
                                            </div>
                                        </div>
                                    )}
                                    {currentItem && currentItem.department === 'I_AM_INTERESTED' && (
                                        <div className="info-interest">
                                            <h5>Imóvel de Interesse</h5>
                                            <p>
                                                <a href={currentItem && renderRealEstateLink(currentItem.realEstateId, currentItem.realEstate.type)} target="_blank" rel="noopener noreferrer">
                                                    {currentItem && currentItem.realEstate.name}
                                                </a>
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="description">
                                    <h5>Mensagem do E-mail</h5>
                                    <p>{currentItem && currentItem.message}</p>
                                </div>
                                <div className='delete'>
                                    <Button isLoading={isLoadingDelete} type="button" placeholder="Excluir" onClick={() => currentItem?.id && deleteContact(Number(currentItem.id))} />
                                </div>
                            </div>
                        </div>
                    </Box>
                </Modal>
            </div>
        </div>
    )
}
